import Image from 'next/image'
import Link from 'next/link'

import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { BLOG_BASE_PATH } from '@/utilities/blogBasePath'
import { formatAuthorJobTitle } from '@/utilities/formatAuthorJobTitle'
import type { Blog, Author, Media } from '@/payload-types'

interface Props {
  blog: Blog
  variant?: 'light' | 'dark'
}

export function BlogFeaturedCard({ blog, variant = 'light' }: Props) {
  const author = blog.author && typeof blog.author === 'object' ? (blog.author as Author) : null
  const avatar = author?.avatar && typeof author.avatar === 'object' ? (author.avatar as Media) : null
  const displayJobTitle = author ? formatAuthorJobTitle(author.jobTitle, author.company) : null

  const publishedDate = blog.publishedAt
    ? new Date(blog.publishedAt).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  const isDark = variant === 'dark'

  return (
    <Link
      href={`${BLOG_BASE_PATH}/${blog.slug}`}
      className={`group flex flex-col justify-between transition-colors duration-300 h-full ${
        isDark
          ? 'p-4 md:min-h-[240px]'
          : 'rounded-[12px] border border-black p-6 hover:bg-black lg:min-h-[320px] xl:min-h-[350px]'
      }`}
    >
      {/* Top section */}
      <div>
        {publishedDate && (
          <p
            className={`body-small transition-colors duration-300 mb-4 ${
              isDark ? 'text-white/70' : 'text-black/70 group-hover:text-white/70'
            }`}
          >
            {publishedDate}
          </p>
        )}
        <h3
          className={`subheading-large xl:subheading-xlarge transition-colors duration-300 line-clamp-3 ${
            isDark ? 'text-white' : 'text-black group-hover:text-white'
          }`}
        >
          {blog.title}
        </h3>
      </div>

      {/* Bottom section */}
      <div className="flex items-end justify-between gap-4 mt-6">
        {/* Author - hidden on mobile for dark variant, visible on md+ */}
        {author && (
          <div
            className={`flex gap-3 ${isDark ? 'hidden md:flex items-center' : 'items-center lg:items-start'}`}
          >
            {avatar?.url ? (
              <Image
                src={avatar.url}
                alt={author.name}
                width={42}
                height={42}
                className={`size-[42px] rounded-full object-cover grayscale border transition-colors duration-300 ${
                  isDark
                    ? 'border-white'
                    : 'border-black group-hover:border-white'
                }`}
              />
            ) : (
              <div
                className={`size-[42px] rounded-full flex items-center justify-center border transition-colors duration-300 ${
                  isDark
                    ? 'bg-white/20 border-white'
                    : 'bg-civo-cloud border-black group-hover:border-white group-hover:bg-white/20'
                }`}
              >
                <span
                  className={`body-small font-medium transition-colors duration-300 ${
                    isDark ? 'text-white' : 'text-black group-hover:text-white'
                  }`}
                >
                  {author.name.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <p
                className={`subheading-xsmall transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-black group-hover:text-white'
                }`}
              >
                {author.name}
              </p>
              {!isDark && displayJobTitle && (
                <p className="body-small text-black/70 group-hover:text-white/70 transition-colors duration-300">
                  {displayJobTitle}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Read more */}
        <span
          className={`subheading-xsmall inline-flex items-center gap-1 transition-colors duration-300 underline underline-offset-2 shrink-0 ${
            isDark ? 'text-white' : 'text-black group-hover:text-white'
          }`}
        >
          Read more
          <ArrowIcon className="shrink-0 size-4 transition-transform duration-300 ease-in-out group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  )
}
